import { E_AlarmLevel, E_AlarmType } from '../enums'
import type { IPageData, IPageParams } from '../types'
import { IAiDiagnosisConclusion } from './ai.types'
import { E_AlarmMode } from './alarm.types'
import { IDiagnosisNewDetailItem } from './diagnosis.types'

/**
 * 人工趋势
 */
export enum E_ManualTrendFlag {
    No = 0,
    Yes = 1
}

/**
 * 算法趋势状态
 * $t('enum.E_AlgorithmTrendStatus.Unrecognized')
 * $t('enum.E_AlgorithmTrendStatus.Success')
 * $t('enum.E_AlgorithmTrendStatus.InsufficientData')
 * $t('enum.E_AlgorithmTrendStatus.Failed')
 */
export enum E_AlgorithmTrendStatus {
    /**
     * 未识别
     */
    Unrecognized = 0,
    /**
     * 成功
     */
    Success = 1,
    /**
     * 数据不足
     */
    InsufficientData = 2,
    /**
     * 失败
     */
    Failed = 3,
}

/**
 * APEF阈值状态
 * 0: 自定义阈值，1: APEF阈值
 */
export enum E_ApefStartStatus {
    /**
     * 自定义阈值
     */
    CustomThreshold = 0,
    /**
     * APEF阈值
     */
    ApefThreshold = 1,
}

export enum E_AlgorithmTrendPrediction {
    Up = 'Up',
    NotEnough = 'NotEnough',
    NotUp = 'NotUp',
}

/** 周统计查询和导出参数，Swagger 中所有查询参数均为可选 */
export interface IAlarmStatisticsQueryParams extends Partial<IPageParams> {
    /** 报警轴向 */
    alarmAxis?: 'X' | 'Y' | 'Z' | 'T' | 'V' | 'NONE'
    /** 报警等级编码 */
    alarmLevelList?: E_AlarmLevel[]
    /** 报警方式 */
    alarmMode?: E_AlarmMode
    /** 报警类型编码 */
    alarmTypeList?: E_AlarmType[]
    /** 区域ID */
    areaId?: number
    /** 公司ID */
    companyId?: number
    /** 结束时间 */
    endTime?: string
    /** 设备ID */
    equipmentId?: number
    /** 主键ID */
    id?: number
    /** 测点ID */
    mappingId?: number
    /** 传感器ID，支持模糊查询 */
    sensorId?: string
    /** 开始时间 */
    startTime?: string
    /** 阈值方式 */
    thresholdMode?: string
    apefStartStatus?: E_ApefStartStatus
}

/** 报警统计列表，响应字段按 Swagger 的非必填定义声明 */
export interface IAlarmStatisticsItem {
    /** AI诊断结论 */    
    aiDiagnosisConclusion?: string // JSON<IAiDiagnosisConclusion>
    /** AI处理建议 */
    aiDiagnosisMeasure?: string
    /** 最新报警轴向编码 */
    alarmAxis?: string
    /** 最新报警轴向名称 */
    alarmAxisName?: string
    /** 报警分类 */
    alarmCategory?: number
    /** 本周该报警类型和轴向的报警次数 */
    alarmCount?: number
    /** 最新报警等级 */
    alarmLevel?: number
    /** 最新报警方式 */
    alarmMode?: number
    /** 最新报警阈值 */
    alarmThreshold?: string
    /** 最新报警时间，用于趋势和图谱跳转 */
    alarmTime?: string
    /** 最新报警类型编码 */
    alarmType?: number
    /** 最新报警类型名称 */
    alarmTypeName?: string
    /** 最新报警值 */
    alarmValue?: string
    /** 算法趋势：0否，1是 */
    algorithmTrendFlag?: E_ManualTrendFlag
    /** 算法原始结果 */
    algorithmTrendPrediction?: E_AlgorithmTrendPrediction
    /** 算法趋势概率 */
    algorithmTrendProbability?: number
    /** 算法趋势状态 */
    algorithmTrendStatus?: E_AlgorithmTrendStatus
    /** 区域ID */
    areaId?: number
    /** 区域名称 */
    areaName?: string
    /** 公司ID */
    companyId?: number
    /** 公司名称 */
    companyName?: string
    /** 最新特征值ID */
    dataId?: number
    /** 设备ID */
    equipmentId?: number
    /** 设备名称 */
    equipmentName?: string
    /** 周统计ID */
    id?: number
    /** 最新报警ID，用于趋势和图谱跳转 */
    latestAlarmId?: number
    /** 人工诊断结论 */
    manualDiagnosisConclusion?: string
    /** 人工趋势：0否，1是 */
    manualTrendFlag?: E_ManualTrendFlag
    /** 测点ID */
    mappingId?: number
    /** 模型诊断结论 */    
    modelDiagnosisConclusion?: string // JSON<IDiagnosisNewDetailItem>
    /** 测点名称 */
    pointName?: string
    /** 传感器ID */
    sensorId?: string
    /** 最新波形ID */
    waveId?: number
    /** 统计周结束时间（date-time） */
    weekEndTime?: string
    /** 统计周开始时间（date-time） */
    weekStartTime?: string

    /**
     * 周期： 2026年第38周
     */
    weekCycle?: string

    apefStartStatus?: E_ApefStartStatus
    parentCompanyId?: number
    parentCompanyName?: string
}

/** Swagger 分页结果包含 pages，不包含公共分页类型中的 orders */
export type IAlarmStatisticsPageData = Partial<Omit<IPageData<IAlarmStatisticsItem>, 'orders'>> & {
    pages?: number
}

export interface IAlarmStatisticsManualUpdateParams {
    /** 周统计ID，必填 */
    id: number
    /** 人工诊断结论；传空字符串可清空，不传表示不修改 */
    manualDiagnosisConclusion?: string
    /** 人工趋势：0否，1是；不传表示不修改 */
    manualTrendFlag?: E_ManualTrendFlag
}
